import { useId } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

/**
 * Reusable toggle row with label, description, and switch.
 *
 * #2422: the switch is named by the row's label (`htmlFor`) and described by
 * its helper line (`aria-describedby`). Without them a screen reader heard
 * "switch, off" with no name on the privacy and notification settings.
 * `useId` keeps two rows on one page from sharing an id.
 */
export function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const id = useId();
  const switchId = `${id}-switch`;
  const descriptionId = `${id}-description`;
  return (
    <div className="flex items-center justify-between">
      <div className="min-w-0 pr-4">
        <Label htmlFor={switchId} className="text-body font-medium">{label}</Label>
        <p id={descriptionId} className="text-caption text-stevens-ink-3 mt-0.5">{description}</p>
      </div>
      <Switch
        id={switchId}
        aria-describedby={descriptionId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}
